/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import type { Table } from '@tanstack/react-table';

import { jsx } from '@atlaskit/css';
import Popup from '@atlaskit/popup';

import { type CharlieTableProps } from '../../charlie-table.types';
import { MoreActionsMenu, type TableActions } from '../more-actions/more-actions-menu';
import { MoreActionsTrigger } from '../more-actions/more-actions-trigger';
import { type ColumnMetadata } from '../more-actions/more-actions.utils';

export interface HeaderMoreActionsProps<T extends object> {
	columnMetadata: ColumnMetadata;
	charlieTableState: Table<T>;
	tableActions: TableActions;
	labels: NonNullable<CharlieTableProps<T>['labels']>;
	allowedColumnActionMoves: Set<'left' | 'right'>;
	columnActions: NonNullable<CharlieTableProps<T>['columnActions']>;
	interactionName?: string;
	analyticsContext?: Record<string, unknown>;
	showMoreActionsButton: boolean;
	isMoreActionsOpened: boolean;
	onToggleMoreActions: () => void;
	onMoreActionsClose: () => void;
	moreActionsLabel: ReactNode;
	onMoreActionsFocusChange: (focused: boolean) => void;
	isSortingActive: boolean;
}

/**
 * More actions popup (column menu) for table header: sort, move, pin, hide.
 */
export const HeaderMoreActions = <T extends object>({
	columnMetadata,
	charlieTableState,
	tableActions,
	labels,
	allowedColumnActionMoves,
	columnActions,
	interactionName,
	analyticsContext,
	showMoreActionsButton,
	isMoreActionsOpened,
	onToggleMoreActions,
	onMoreActionsClose,
	moreActionsLabel,
	onMoreActionsFocusChange,
	isSortingActive,
}: HeaderMoreActionsProps<T>) => (
	<Popup
		content={() => (
			<MoreActionsMenu
				column={columnMetadata}
				tableState={charlieTableState.getState()}
				tableActions={tableActions}
				closeMenu={onMoreActionsClose}
				labels={labels}
				allowedColumnMoves={allowedColumnActionMoves}
				columnActions={columnActions}
				interactionName={interactionName}
			/>
		)}
		onClose={onMoreActionsClose}
		isOpen={isMoreActionsOpened}
		placement="bottom-start"
		trigger={(popupTriggerProps) => (
			<MoreActionsTrigger
				popupTriggerProps={popupTriggerProps}
				showMoreActionsButton={showMoreActionsButton}
				onToggleMoreActions={onToggleMoreActions}
				interactionName={interactionName}
				analyticsContext={analyticsContext}
				isMoreActionsOpened={isMoreActionsOpened}
				moreActionsLabel={moreActionsLabel}
				onFocus={() => onMoreActionsFocusChange(true)}
				onBlur={() => onMoreActionsFocusChange(false)}
				isSortingActive={isSortingActive}
			/>
		)}
	/>
);
